import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { Wind, AlertTriangle, Activity, Download, RotateCcw, Sparkles, Loader2, CheckCircle2, XCircle } from 'lucide-react';

import { API_URL } from '../config';

export default function VNIPage() {
  const { token } = useAuth();
  const printRef = useRef(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('vni-data');
      return saved ? JSON.parse(saved) : {
        contrindications: [],
        indications: [],
        pathologie: '',
        parametres: { fio2: '100', ai: '6', pep: '5', vt: '6' }
      };
    } catch {
      return { contrindications: [], indications: [], pathologie: '', parametres: { fio2: '100', ai: '6', pep: '5', vt: '6' } };
    }
  });

  useEffect(() => {
    localStorage.setItem('vni-data', JSON.stringify(formData));
  }, [formData]);

  const handleAnalyze = async () => {
    try {
      setIsAnalyzing(true);
      toast.info('Analyse en cours...');
      const response = await fetch(`${API_URL}/api/ai/analyze-vni`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Erreur');
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('Analyse terminee');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const CI = ['Reanimation ACR', 'Obstruction VAS', 'Trauma cranio-facial grave', 'Vomissements incoercibles', 'Hemorragie digestive haute', 'Epuisement respiratoire'];
  const indications = ['BPCO decompense', 'OAP', 'SOH (obesite-hypoventilation)', 'Pre-oxygenation ISR/IOT'];

  const hasCI = formData.contrindications.length > 0;

  return (
    <div className="p-3 md:p-6 space-y-3 max-w-5xl mx-auto">
      <div className="space-y-2 print:hidden">
        <div className="flex justify-between items-start gap-2">
          <div className="flex items-center gap-2">
            <Wind className="w-6 h-6 text-cyan-400" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-white">VNI</h1>
              <p className="text-xs text-gray-400">Ventilation Non Invasive Adulte</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAnalyze} disabled={isAnalyzing} size="sm" className="bg-purple-500/20">
              {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </Button>
            <Button onClick={() => { localStorage.removeItem('vni-data'); window.location.reload(); }} size="sm" variant="outline">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {hasCI && (
          <Alert className="bg-red-500/20 border-red-500 p-2 animate-pulse">
            <XCircle className="h-4 w-4" />
            <AlertDescription className="text-xs text-red-300 font-bold">CONTRE-INDICATION PRESENTE - DISCUTER IOT</AlertDescription>
          </Alert>
        )}
      </div>

      <div ref={printRef}>
        <Tabs defaultValue="ci">
          <TabsList className="grid w-full grid-cols-3 bg-gray-800">
            <TabsTrigger value="ci" className="text-xs">CI</TabsTrigger>
            <TabsTrigger value="indications" className="text-xs">Indications</TabsTrigger>
            <TabsTrigger value="parametres" className="text-xs">Parametres</TabsTrigger>
          </TabsList>

          <TabsContent value="ci" className="space-y-3 mt-3">
            <Card className="border-red-500/30 bg-red-500/5">
              <CardHeader className="p-3">
                <CardTitle className="text-sm text-red-300 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />CONTRE-INDICATIONS
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-2">
                {CI.map((ci, i) => (
                  <label key={i} className="flex items-center gap-2 p-2 rounded bg-gray-800 cursor-pointer">
                    <Checkbox
                      checked={formData.contrindications.includes(ci)}
                      onCheckedChange={() => {
                        const newCI = formData.contrindications.includes(ci)
                          ? formData.contrindications.filter(x => x !== ci)
                          : [...formData.contrindications, ci];
                        setFormData(prev => ({ ...prev, contrindications: newCI }));
                      }}
                    />
                    <span className="text-xs text-white">{ci}</span>
                  </label>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="indications" className="space-y-3 mt-3">
            <Card className="border-green-500/30 bg-green-500/5">
              <CardHeader className="p-3">
                <CardTitle className="text-sm text-green-300 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />INDICATIONS
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-2">
                {indications.map((ind, i) => (
                  <button
                    key={i}
                    onClick={() => setFormData(prev => ({ ...prev, pathologie: ind, indications: [ind] }))}
                    className={`w-full text-left p-3 rounded border transition-all ${
                      formData.pathologie === ind
                        ? 'bg-green-500/20 border-green-500'
                        : 'bg-gray-800 border-gray-700'
                    }`}
                  >
                    <span className="text-sm text-white font-medium">{ind}</span>
                  </button>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="parametres" className="space-y-3 mt-3">
            <Card className="border-cyan-500/30">
              <CardHeader className="p-3">
                <CardTitle className="text-sm text-cyan-300">MODE: VS-AI-PEP</CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">FiO2 (%)</label>
                    <Input type="number" value={formData.parametres.fio2} onChange={(e) => setFormData(prev => ({ ...prev, parametres: { ...prev.parametres, fio2: e.target.value } }))} className="h-9" />
                    <p className="text-xs text-green-400 mt-1">Objectif SpO2 88-92% (BPCO) ou &gt;92% (OAP)</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">AI (cmH2O)</label>
                    <Input type="number" value={formData.parametres.ai} onChange={(e) => setFormData(prev => ({ ...prev, parametres: { ...prev.parametres, ai: e.target.value } }))} className="h-9" />
                    <p className="text-xs text-cyan-300 mt-1">BPCO: 6-15 | OAP: 6-20</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">PEP (cmH2O)</label>
                    <Input type="number" value={formData.parametres.pep} onChange={(e) => setFormData(prev => ({ ...prev, parametres: { ...prev.parametres, pep: e.target.value } }))} className="h-9" />
                    <p className="text-xs text-cyan-300 mt-1">BPCO: 5-12 | OAP: 5-10 puis 10-12</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Vt (ml/kg)</label>
                    <Input type="number" value={formData.parametres.vt} onChange={(e) => setFormData(prev => ({ ...prev, parametres: { ...prev.parametres, vt: e.target.value } }))} className="h-9" />
                    <p className="text-xs text-cyan-300 mt-1">Objectif: 6-8 ml/kg</p>
                  </div>
                </div>

                {formData.pathologie && (
                  <Alert className="bg-blue-500/10 border-blue-500/30 p-3">
                    <Activity className="h-4 w-4" />
                    <AlertDescription className="text-xs text-blue-300">
                      <strong>PATHOLOGIE: {formData.pathologie}</strong><br/>
                      {formData.pathologie.includes('BPCO') && 'AI: 6-15 | PEP: 5-12 | SpO2: 88-92% | Trigger: 50%'}
                      {formData.pathologie.includes('OAP') && 'AI: 6-20 | PEP: 5 puis 10-12 | SpO2: >92% | Trigger: 25-30%'}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {aiAnalysis && (
          <Card className="border-purple-500/30 bg-purple-500/5 mt-3">
            <CardHeader className="p-3">
              <CardTitle className="text-sm text-purple-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />ANALYSE IA
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="whitespace-pre-wrap text-xs text-white">{aiAnalysis.analysis}</div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
